import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "optometry-web-design-colorado",
  metaTitle: "Optometry Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom optometry web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Optometry Web Design Colorado",
  eyebrow: "Optometry Web Design — Colorado",
  h1: (
    <>
      Optometry <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded optometry websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Optometry websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first optometry sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Optometry web design in Colorado",
    body: "Optometry web design is the practice of building conversion-focused, mobile-first websites for optometry businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with online booking above the fold." },
    { title: "Service pages per visit type", body: "Eye exam, contacts, kids, dry eye, glaucoma, diabetic — each ranks independently." },
    { title: "Online booking integration", body: "Real-time booking integration with RevolutionEHR, Compulink, or your platform." },
    { title: "Insurance & frame brand pages", body: "Accepted vision plans and carried frame brands surfaced where patients look." },
    { title: "Provider bio pages", body: "Each OD with credentials, specialties, and direct booking CTA." },
    { title: "Schema and SEO at launch", body: "MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Insurance clarity drives the booking", body: "Accepted vision plans (VSP, EyeMed, etc.) above the fold cuts front-desk calls and drives bookings." },
    { heading: "Frame brand pages capture branded search", body: "Searchers Google \"Oakley dealer\", \"Ray-Ban prescription\" — capture that traffic with brand pages." },
    { heading: "Specialty exams drive premium revenue", body: "Dry eye clinic, scleral lenses, and myopia management deserve dedicated pages." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a optometry website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a optometry website?", a: "Typical timeline is 4–6 weeks for a 12–20 page optometry site with full service and service-area pages. Larger multi-location optometry businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs optometry businesses?", a: "No — we build optometry sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Optometry Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for optometry businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/optometry-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
